import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import NumberFormat from 'react-number-format';
import { CircularProgress } from '@material-ui/core';
import currencySymbol from '../../../../helpers/currencySymbol';
import { SearchStatus, TourUnitPricingModelKinds } from '../../../../constants';
import { getTourRateOption } from '../../../../helpers/getTourOptions';
import getTourRateChoice from '../../../../helpers/getTourRateChoice';
import readTourRateLimit from '../../../../helpers/readTourRateLimit';
import useTourSchedule, { slotKey } from '../../../../hooks/useTourSchedule';
import useTourQuote from '../../../../hooks/useTourQuote';
import TourRateSelect from './TourRateSelect';
import TourSingleRateSelect from './TourSingleRateSelect';
import TourDateSelect from './TourDateSelect';

const slotLabel = (slot) => {
  const parts = [slot.time, slot.description];
  const label = parts.filter(Boolean).join(' — ');
  return label || 'Any time';
};

const TourBookingPanel = ({ itinerary, details, onAddToTrip, isAddDisabled, doShowPrice }) => {
  const router = useRouter();
  const isBrochurePage = router.pathname.indexOf('brochure') > -1;
  const [quantities, setQuantities] = useState({});
  const [pickedOption, setPickedOption] = useState(null);

  const availability = details?.availability;
  const minQuantity = availability?.minQuantity;
  const maxQuantity = availability?.maxQuantity;
  const schedule = useTourSchedule(itinerary.productId, details);
  const {
    days,
    month,
    selectedDate,
    selectedSlotKey,
    day,
    slot,
    slots,
    slotIndex,
    isLoading: isLoadingCalendar,
    isMonthEmpty,
  } = schedule;
  const productRates = itinerary.tour?.rates?.length ? itinerary.tour.rates : availability?.rates;
  const choice = getTourRateChoice({
    productRates,
    offeredRates: slot?.rates?.length ? slot.rates : availability?.rates,
    pricingModelKind: availability?.pricingModelKind,
    pickedOption,
    pricedRateQuantities: availability?.pricedRateQuantities,
  });
  const { options, rates, isSingleChoice } = choice;
  const selectedOption = choice.option;
  const keepOfferedRates = (source) =>
    Object.fromEntries(
      Object.entries(source).filter(([name]) =>
        rates.some((rate) => (rate.parameterName || rate.name) === name),
      ),
    );
  const party = keepOfferedRates(quantities);
  const {
    isQuoted,
    quotedDay,
    quotedSlot,
    isQuoting,
    error: quoteError,
    requestQuote,
  } = useTourQuote(itinerary.productId, details, {
    date: selectedDate,
    slot,
    rateQuantities: party,
  });

  useEffect(() => {
    const priced = availability?.pricedRateQuantities;
    if (Object.keys(quantities).length || !priced || !Object.keys(priced).length) {
      return;
    }
    setQuantities(priced);
    // oxlint-disable-next-line react/exhaustive-deps
  }, [availability?.pricedRateQuantities]);

  const handleMonthChange = (next) => schedule.selectMonth(next, party);

  const handleOptionChange = (option) => {
    const opening = (productRates || []).find(
      (rate) => getTourRateOption(rate) === option && (rate.minAge != null || rate.maxAge != null),
    );
    setPickedOption(option);
    setQuantities({});
    schedule.reset();
    schedule.loadMonth(
      month,
      opening
        ? { [opening.parameterName || opening.name]: Math.max(availability?.minQuantity || 1, 1) }
        : undefined,
      true,
    );
  };

  const handleDateChange = (date) => {
    const firstSlot = schedule.selectDate(date);
    requestQuote(date, firstSlot, party);
  };

  const handleSlotChange = (key) => {
    const picked = schedule.selectSlot(key);
    requestQuote(selectedDate, picked, party);
  };

  const handleQuantityChange = (parameterName, value) => {
    const updated = isSingleChoice ? {} : { ...quantities };
    updated[parameterName] = value;
    if (!value) {
      delete updated[parameterName];
    }

    setQuantities(updated);
    requestQuote(selectedDate, slot, keepOfferedRates(updated));
  };

  const handleRateChange = (parameterName) => {
    const picked = rates.find((rate) => (rate.parameterName || rate.name) === parameterName);
    const count = Math.max(picked?.minQuantity || minQuantity || 1, 1);
    handleQuantityChange(parameterName, parameterName ? count : 0);
  };

  // Only a quote for the exact date, slot and party is a price; the calendar price is for the default party.
  const price = quotedSlot?.price ?? quotedDay?.price;
  const currency =
    quotedSlot?.currency ||
    quotedDay?.currency ||
    slot?.currency ||
    day?.currency ||
    itinerary.tour?.currency;
  const dueNow = quotedDay?.amountDueAtBooking;
  const totalTravelers = Object.values(party).reduce((sum, value) => sum + value, 0);
  const offeredMaxQuantity = maxQuantity ?? readTourRateLimit(rates, 'maxQuantity');
  const isUnitPriced = TourUnitPricingModelKinds.includes(availability?.pricingModelKind);
  const requiredMinQuantity = isUnitPriced
    ? readTourRateLimit(rates, 'minQuantity')
    : (minQuantity ?? readTourRateLimit(rates, 'minQuantity'));
  const countLabel = isUnitPriced ? 'Units' : '# Persons';
  const isReady = !!day && !!slot && totalTravelers > 0;
  const partyKey = JSON.stringify(party);

  useEffect(() => {
    if (isReady && !isQuoted && !isQuoting && !quoteError) {
      requestQuote(selectedDate, slot, party);
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, [
    isReady,
    selectedDate,
    slot?.time,
    slot?.description,
    partyKey,
    isQuoted,
    isQuoting,
    quoteError,
  ]);
  const isBelowMinimum = totalTravelers < Math.max(requiredMinQuantity || 1, 1);
  const isAboveMaximum = !!offeredMaxQuantity && totalTravelers > offeredMaxQuantity;
  const dueOnDay = dueNow != null && price != null ? price - dueNow : null;
  const renderAmount = (value) => (
    <NumberFormat
      value={value || 0}
      displayType="text"
      thousandSeparator
      decimalScale={2}
      fixedDecimalScale
      prefix={currencySymbol(currency)}
    />
  );
  const isOnlyQuotable = itinerary.tour?.isOnlyQuotable === true;
  const isAddable =
    isReady &&
    !isBelowMinimum &&
    !isAboveMaximum &&
    !isAddDisabled &&
    !isQuoting &&
    !quoteError &&
    price != null &&
    !isOnlyQuotable;

  const selection = {
    day,
    slot,
    quantities: party,
    guests: totalTravelers,
    price,
    amountDueAtBooking: dueNow,
    currency,
    pricingModelKind: availability?.pricingModelKind,
  };

  const optionSelect = options.length > 0 && (
    <div className="mb-2">
      <div className="text-gray-600">Option</div>
      <select
        className="border rounded px-2 py-1 w-full text-xs"
        value={selectedOption || ''}
        onChange={(event) => handleOptionChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );

  const isLoadingDates =
    !days.length && (isLoadingCalendar || details?.status === SearchStatus.SEARCHING);
  if (isLoadingDates) {
    return (
      <div className="bg-white rounded-md shadow p-3 text-center min-h-16">
        <CircularProgress color="secondary" size={24} />
      </div>
    );
  }

  const calendar = (
    <TourDateSelect
      days={days}
      value={selectedDate}
      month={month}
      onChange={handleDateChange}
      onMonthChange={handleMonthChange}
      isLoading={isLoadingCalendar}
    />
  );

  if (!days.length) {
    return (
      <div className="bg-white rounded-md shadow p-3 text-sm text-gray-600 min-h-16">
        {optionSelect}
        <div className="mb-2">
          {details?.availabilityMessage || 'No dates are available for this option.'}
        </div>
        {calendar}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-md shadow p-3 text-sm">
      {optionSelect}

      {calendar}

      {details?.calendarError && (
        <div className="mt-1 text-red-600">Dates could not be loaded for this month.</div>
      )}

      {isMonthEmpty && (
        <div className="mt-1 text-gray-600">
          {details?.availabilityMessage ||
            `The operator has no departures in ${month.format('MMMM YYYY')}.`}
        </div>
      )}

      {slots.length > 0 && (
        <div className="mt-2">
          <div className="text-gray-600">Time</div>
          <select
            className="border rounded px-2 py-1 w-full text-xs"
            value={selectedSlotKey || (slot ? slotKey(slot, slotIndex) : '')}
            onChange={(event) => handleSlotChange(event.target.value)}
          >
            {slots.map((entry, index) => (
              <option key={slotKey(entry, index)} value={slotKey(entry, index)}>
                {slotLabel(entry)}
              </option>
            ))}
          </select>
          {!!slot?.placesRemaining && (
            <div className="mt-1 text-right text-xs text-red-600">
              {`${slot.placesRemaining} places remaining.`}
            </div>
          )}
        </div>
      )}

      <div className="mt-2 border-t pt-2">
        <div className="mb-1 text-right text-xs text-gray-600">{countLabel}</div>
        {isSingleChoice ? (
          <TourSingleRateSelect
            rates={rates}
            pickedRate={Object.keys(party)[0] || ''}
            value={Object.values(party)[0] || 0}
            minQuantity={minQuantity}
            maxQuantity={maxQuantity}
            onRateChange={handleRateChange}
            onChange={handleQuantityChange}
          />
        ) : (
          <>
            {rates.map((rate) => (
              <TourRateSelect
                key={rate.parameterName || rate.name}
                rate={rate}
                value={party[rate.parameterName || rate.name] || 0}
                minQuantity={minQuantity}
                maxQuantity={maxQuantity}
                onChange={handleQuantityChange}
              />
            ))}
          </>
        )}
      </div>

      {(requiredMinQuantity || offeredMaxQuantity) && (
        <div className="mt-2 text-gray-600 text-xs">
          {requiredMinQuantity ? `Minimum quantity: ${requiredMinQuantity}` : ''}
          {requiredMinQuantity && offeredMaxQuantity ? <br /> : null}
          {offeredMaxQuantity ? `Maximum quantity: ${offeredMaxQuantity}` : ''}
        </div>
      )}

      {itinerary.cancellationPolicy && (
        <div className="mt-1 text-green-700">{itinerary.cancellationPolicy}</div>
      )}

      <div className="mt-2 border-t pt-2">
        <div className="flex items-baseline justify-between">
          <span className="font-semibold">{doShowPrice ? 'Total price:' : 'Your selection:'}</span>
          {isQuoting || (isReady && !isQuoted && !quoteError) ? (
            <CircularProgress color="secondary" size={16} />
          ) : (
            <span className="text-lg font-bold">
              {(() => {
                if (!isReady) {
                  return (
                    <span className="text-sm font-normal text-gray-500">
                      Pick a date and travelers
                    </span>
                  );
                }
                if (isQuoted && price == null) {
                  return (
                    <span className="text-sm font-normal text-red-600">
                      No price for this date and party
                    </span>
                  );
                }
                return doShowPrice ? (
                  renderAmount(price || 0)
                ) : (
                  <span className="text-sm font-normal text-gray-700">Ready to book</span>
                );
              })()}
            </span>
          )}
        </div>
        {doShowPrice && isReady && dueNow != null && (
          <div className="mt-2 rounded bg-amber-50 p-2 text-sm text-amber-900">
            <div className="flex items-baseline justify-between">
              <span>Charged now:</span>
              <span className="font-bold">{renderAmount(dueNow)}</span>
            </div>
            <div className="mt-1 flex items-baseline justify-between">
              <span>To the operator on the tour day:</span>
              <span className="font-bold">{renderAmount(dueOnDay)}</span>
            </div>
          </div>
        )}
      </div>

      {quoteError && <div className="mt-2 text-red-600">{quoteError}</div>}

      {isOnlyQuotable && (
        <div className="mt-2 text-amber-800">
          The operator quotes this tour but does not sell it through the API, so it can be shown to
          a traveller and not booked here.
        </div>
      )}

      <button
        type="button"
        className={clsx('w-full mt-2 py-2 rounded font-bold focus:outline-none', {
          'bg-theme text-white': isAddable,
          'bg-gray-200 text-gray-500 cursor-not-allowed': !isAddable,
        })}
        disabled={!isAddable}
        onClick={(event) => onAddToTrip(selection, event)}
      >
        {isBrochurePage ? 'Book' : 'Add to trip'}
      </button>
    </div>
  );
};

TourBookingPanel.propTypes = {
  itinerary: PropTypes.shape().isRequired,
  details: PropTypes.shape(),
  onAddToTrip: PropTypes.func.isRequired,
  isAddDisabled: PropTypes.bool,
  doShowPrice: PropTypes.bool,
};

TourBookingPanel.defaultProps = {
  details: undefined,
  isAddDisabled: false,
  doShowPrice: true,
};

export default TourBookingPanel;
